import { useState, useEffect, useRef } from 'react'
import { useServices } from '../../core/services/ServicesContext'
import type { Flow } from '../../core/domain/models'

/**
 * Compares two Flow arrays by length and individual object identity.
 * getFlowsByScenario() always returns a new array, but the individual
 * Flow objects are the same references when unchanged. This avoids
 * triggering React re-renders when the array is reconstructed with
 * identical contents.
 */
export function areFlowArraysEqual(prev: Flow[], next: Flow[]): boolean {
  if (prev === next) return true
  if (prev.length !== next.length) return false
  for (let i = 0; i < prev.length; i++) {
    if (prev[i] !== next[i]) return false
  }
  return true
}

export function useFlows(scenarioId: string) {
  const { memoryState } = useServices()
  const [flows, setFlows] = useState<Flow[]>([])
  const [loading, setLoading] = useState(true)
  const flowsRef = useRef<Flow[]>([])

  useEffect(() => {
    if (!scenarioId) {
      flowsRef.current = []
      setFlows([])
      setLoading(false)
      return
    }

    const updateFlowsIfChanged = (nextFlows: Flow[]) => {
      if (!areFlowArraysEqual(flowsRef.current, nextFlows)) {
        flowsRef.current = nextFlows
        setFlows(nextFlows)
      }
    }

    const loadFlows = async () => {
      try {
        setLoading(true)
        
        // Note: We rely on ScenarioLayout (or parent) to hydrate the scenario
        // This hook just reads state reactively
        const scenarioFlows = memoryState.getFlowsByScenario(scenarioId)
        updateFlowsIfChanged(scenarioFlows)
      } catch (error) {
        console.error('Error loading flows:', error)
        flowsRef.current = []
        setFlows([])
      } finally {
        setLoading(false)
      }
    }

    loadFlows()

    // Subscribe to state changes
    const unsubscribe = memoryState.onStateChange(scenarioId, () => {
      const scenarioFlows = memoryState.getFlowsByScenario(scenarioId)
      updateFlowsIfChanged(scenarioFlows)
    })

    return () => unsubscribe()
  }, [scenarioId, memoryState])

  return { flows, loading }
}

export function useFlow(scenarioId: string, flowId: string) {
  const { memoryState } = useServices()
  const [flow, setFlow] = useState<Flow | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!scenarioId || !flowId) {
      setFlow(null)
      setLoading(false)
      return
    }

    const loadFlow = async () => {
      try {
        setLoading(true)
        
        // Note: We rely on ScenarioLayout (or parent) to hydrate the scenario
        // This hook just reads state reactively
        const flowData = memoryState.getFlow(flowId)
        setFlow(flowData || null)
      } catch (error) {
        console.error('Error loading flow:', error)
        setFlow(null)
      } finally {
        setLoading(false)
      }
    }

    loadFlow()

    // Subscribe to state changes
    const unsubscribe = memoryState.onStateChange(scenarioId, () => {
      const flowData = memoryState.getFlow(flowId)
      setFlow(flowData || null)
    })

    return () => unsubscribe()
  }, [scenarioId, flowId, memoryState])

  return { flow, loading }
}
